import React from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, Form, OverlayTrigger } from "react-bootstrap";
import { useFetchImages } from "../../hooks/useFetchImages";
import "./ImageItemResult.scss";
import Tooltip from "react-bootstrap/Tooltip";
import { IMetadataModel } from "../../types/pinecone";
import { useConfigState, useExtractedImagesState } from "../../hooks";
import { Link } from "react-router-dom";
import { copyFilenameToClipboard } from "../../utils/manualFinderUtils";
import { set } from "lodash";

interface IImageItemResultProps {
    index: number;
    metadata: IMetadataModel;
    elements?: any[] | null;
    onUnmatchClick?: () => void;
    onMatchClick?: () => void;
    score?: number;
    isDisplayConfig?: boolean;
    checked: boolean[];
    setChecked: (checked: boolean[]) => void;
}

export const ImageItemResult: React.FC<IImageItemResultProps> = ({
    index,
    metadata,
    elements,
    onUnmatchClick,
    onMatchClick,
    score,
    isDisplayConfig,
    checked,
    setChecked
}) => {
    const { t } = useTranslation();
    const { img, imgTA } = useFetchImages(`processed_images/${metadata.FileName}.png`);
    const { showDistance } = useConfigState();

    const {
        addToSelectedImages,
        removeFromSelectedImages,
    } = useExtractedImagesState()

    const renderTooltip = (props: any) => (
        <Tooltip id="button-tooltip" {...props}>
            {t("MatchFunctionality.MatchedWithTool")}
        </Tooltip>
    );

  const handleCheckboxChange = (filename: string, index: number) => {
        const newChecked = [...checked];
        const isCurrentlyChecked = checked[index];
        const baseFilename = filename.split('.')[0];
        
        newChecked[index] = !isCurrentlyChecked;
        setChecked(newChecked);
        
        if (isCurrentlyChecked) {
            removeFromSelectedImages(baseFilename);
        } else {
            addToSelectedImages(baseFilename);
        }
    };

    return (
        <div className="card-with-image position-relative pt-3">
            {isDisplayConfig &&
                <div className="position-absolute d-flex flex-row justify-content-center gap-1 z-1 item-config-badges">
                    {showDistance && score !== undefined && (
                        <Badge bg="secondary" className="score-badge">
                            {score.toFixed(3)}
                        </Badge>
                    )}
                    {metadata.matchedBy === "ImageMatcherTool" && <OverlayTrigger
                        trigger={["hover", "focus"]}
                        placement="right"
                        delay={{ show: 250, hide: 400 }}
                        overlay={renderTooltip}
                    >
                        <Badge bg="warning" text="dark" className="match-badge">
                            Matched
                        </Badge>
                    </OverlayTrigger>}
                    {elements && (
                        <Badge bg="primary" className="elements-badge">
                            {elements.length}
                        </Badge>
                    )}
                </div>
            }
            <Card className={imgTA ? "card-wrap-double-images" : "card-wrap-image"}>
                <Card.Body className="d-flex flex-column gap-2">
                    <Card.Img variant="top" src={img} alt={metadata.FileName} />
                    {imgTA && <Card.Img variant="top" src={imgTA}
                        alt={metadata.FileName.replace("OEM", "TA")} />}
                </Card.Body>
                <Card.Footer>
                    {elements && (
                        <Link to={metadata.Manufacturer}>
                            <Button variant="secondary">
                                {t("Button.showProductCategorieResults").replace("{}", elements.length.toString())}
                            </Button>
                        </Link>
                    )}
                    <div className="d-flex justify-content-between align-items-center">
                        {t("brand")}: {metadata.Manufacturer}
                        <div className="d-flex justify-content-end me-1">
                            <Form.Check
                                type="checkbox"
                                checked={!!checked[index]}
                                onChange={() => handleCheckboxChange(metadata.FileName, index)}
                                className="custom-checkbox ms-2"
                            /></div>
                    </div>
                    <div className="filename"
                        onDoubleClick={() => copyFilenameToClipboard(metadata.FileName, t("CopyToClipboardMessage"))}>
                        {metadata.FileName.replace("-OEM", "")}
                    </div>
                    {metadata.FileName.endsWith("-Single") && onMatchClick && (
                        <Button variant="outline-secondary" size="sm" className="mt-2" onClick={onMatchClick}>
                            {t("Button.match")}
                        </Button>
                    )}
                    {metadata.matchedBy === "ImageMatcherTool" && onUnmatchClick && (
                        <Button variant="outline-secondary" size="sm" className="mt-2" onClick={onUnmatchClick}>
                            {t("Button.unmatch")}
                        </Button>
                    )}
                </Card.Footer>
            </Card>
        </div>

    );
};
